/**
 * lib/format.ts
 * Lightweight format helpers for currency and dates.
 * - Ensures Gregorian calendar for all Arabic dates.
 */

/**
 * formatCurrency
 * Formats number as currency (default SAR) in Arabic locale.
 */
export function formatCurrency(
  amount: number,
  currency: string = 'SAR',
  locale: string = 'ar'
) {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0)
  } catch {
    return `${Number(amount || 0).toFixed(2)} ${currency}`
  }
}

/**
 * formatDate
 * Formats a date in Arabic using the Gregorian calendar (not Hijri).
 * Uses Intl.DateTimeFormat with calendar: 'gregory'.
 */
export function formatDate(date: string | Date, locale: string = 'ar') {
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return '-'
  try {
    return new Intl.DateTimeFormat(locale, { calendar: 'gregory' }).format(d)
  } catch {
    // Fallback to default toLocaleDateString with Arabic numbers
    return d.toLocaleDateString('ar')
  }
}

/**
 * toISOAtStartOfDay
 * Takes HTML date input value (YYYY-MM-DD) and returns ISO string at 00:00 local time.
 * Hardened: returns input as-is if invalid to avoid RangeError.
 */
export function toISOAtStartOfDay(dateInput: string) {
  // If input is not YYYY-MM-DD, try Date(...) else return input
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    const fallback = new Date(dateInput)
    if (!Number.isNaN(fallback.getTime())) {
      try {
        return fallback.toISOString()
      } catch {
        return dateInput
      }
    }
    return dateInput
  }
  const d = new Date(`${dateInput}T00:00:00`)
  if (Number.isNaN(d.getTime())) return dateInput
  try {
    return d.toISOString()
  } catch {
    return dateInput
  }
}
